/**
 * TA 的性别与生日的读取与缓存（GET／PUT /pets/{pet_id}/facts）。
 * - 查询键按账号 + 宠物隔离，放在 ["pets", …] 下：切换宠物时家庭上下文按现有规则把它清掉（clearPetScoped），不会串到另一只。
 * - 保存后证件（居民证、护照）上的“性别”“出生日期”两行会变：卡包列表与证件详情的缓存一起失效，下次打开重新读。
 */
import { useQuery, type QueryClient } from "@tanstack/react-query";
import type { HouseholdPetBrief } from "@/shared/contracts";
import { queryKeys } from "@/shared/query/queryClient";
import { useServices } from "@/shared/services/registry";
import { useOptionalCurrentHousehold } from "@/shared/session/householdContext";

/** 作息里按“家”算出的时区（basis 为 home）；按驿站、居所（residence）或不知道在哪（unknown）算的不当成家的时区。 */
function homeZoneOf(pet: HouseholdPetBrief | undefined): string | null {
  const rhythm = pet?.life?.rhythm;
  return rhythm?.basis === "home" && rhythm.timezone ? rhythm.timezone : null;
}

/**
 * TA 家的时区（“只知道大概几岁”换算今年用）：家庭列表里这只宠物作息（life.rhythm）按家算出的 timezone
 * （后端 web_runtime/rhythm.py 的 facts()：不知道在哪、或时区认不出时给 null，不拿香港兜底）；
 * 同一个家的宠物住同一个家，这只还没有时就取同一个家里按家算作息的任一只。拿不到（演示、首只还没入住、只有驿站的时区）为 null——
 * 这时不从岁数推年份（c84a 1745Z PET-FACT-01），不借浏览器时区，也不为此另找地点服务。
 */
export function useHomeTimeZone(petId: string | null): string | null {
  const pets = useOptionalCurrentHousehold()?.household?.pets ?? [];
  const own = petId ? pets.find((pet) => pet.pet_id === petId) : undefined;
  return homeZoneOf(own) ?? pets.map(homeZoneOf).find((zone) => zone !== null) ?? null;
}

export function factsKey(userId: string | null, petId: string) {
  return ["pets", "facts", userId ?? "-", petId] as const;
}

/** 每次进来都重新读：家人可能刚改过，拿旧版本号进编辑只会被 409 拒。 */
export function usePetFacts(petId: string, userId: string | null) {
  const { pets } = useServices();
  return useQuery({ queryKey: factsKey(userId, petId), queryFn: ({ signal }) => pets.facts(petId, signal), staleTime: 0 });
}

/** 证件上的两行跟着变：卡包列表（["credentials","list",…]）与证件详情（["life","credential",…]）一起失效。 */
export function invalidateCredentials(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: queryKeys.credentials });
  void queryClient.invalidateQueries({ queryKey: ["life", "credential"] });
}
